import { Button, cn } from "@win-reality/react-ui";
import { useRef, useState } from "react";

import { CONTENT_WIDTH } from "../../constants/layout";
import { usePitchTaggingHotkeys } from "../../hooks/usePitchTaggingHotkeys";
import { Activity, GameFullResponse } from "../../types/gameFull";
import { PitchTags } from "../../utils/pitchTagging";
import {
  formatPitchType,
  getPitchMatchup,
} from "../../utils/plateAppearanceFeed";
import { getRoleColors, TeamDisplays } from "../../utils/teamColors";
import { PitchMetrics } from "../PitchMetrics";
import { HotkeyBadge } from "./OptionButton";
import { PitchHeader } from "./PitchHeader";
import { SECTION_OPTIONS } from "./sections";
import { TagSections } from "./TagSections";
import { usePitchTagsForm } from "./usePitchTagsForm";

type PitchTaggingProps = {
  data: GameFullResponse;
  /** Null while waiting on the pitch (the "Waiting on pitch..." row) */
  activity: Activity | null;
  /** Team colors, for the pitcher and hitter labels */
  teamDisplays: TeamDisplays;
  onSave: (activityId: string, tags: PitchTags) => Promise<unknown> | void;
  /** The previous pitch's tag is still on its way to the game service */
  isSaveDisabled?: boolean;
  /** Opens the End Game confirmation */
  onEndGame: () => void;
};

/**
 * Pitch Details panel: the scorekeeper marks human-observed details in order
 * (pitch type → pitch result → contact type → play result → results).
 * Render keyed by PA + pitch number so the form resets per pitch, while picks
 * made on the waiting row carry over once that pitch arrives.
 */
export const PitchTagging = ({
  data,
  activity,
  teamDisplays,
  onSave,
  isSaveDisabled = false,
  onEndGame,
}: PitchTaggingProps) => {
  const isWaiting = !activity;
  const { sport } = data.game;
  const form = usePitchTagsForm(data, activity);
  const { tags, activeSection } = form;

  // Lives here so unsaved tags survive a trip to the metrics view
  const [view, setView] = useState<"tags" | "metrics">("tags");
  const panelRef = useRef<HTMLDivElement>(null);
  const matchup = getPitchMatchup(data, activity);
  const { hitterColor, pitcherColor } = getRoleColors(
    matchup.isTopOfInning,
    teamDisplays.away.color,
    teamDisplays.home.color,
  );

  // Nothing to save until the hold has filled in the suggestions
  const canSave = activity && !isSaveDisabled && !form.isHolding;
  // The body is still loading, but the call is often known already: its keys
  // end the hold. Results has no options; with every section collapsed only
  // Enter is live
  const optionSection = form.isHolding
    ? "pitchResult"
    : activeSection && activeSection !== "results"
      ? activeSection
      : null;
  // Saves in the background: the pitch's row shows it pending. Tags must
  // reach the game service in pitch order, so the next save waits for the
  // previous one
  const save = () => activity && onSave(activity.activityId, tags);

  usePitchTaggingHotkeys({
    enabled: view === "tags",
    options: optionSection ? SECTION_OPTIONS[sport][optionSection] : [],
    isOptionDisabled: (value) =>
      !optionSection || form.isOptionDisabled(optionSection, value),
    onSelect: (value) => {
      if (optionSection) form.select(optionSection, value);
    },
    onNextSection: form.goToNextSection,
    onPreviousSection: form.goToPreviousSection,
    onSave: canSave ? save : undefined,
    panelRef,
  });

  return (
    <div
      ref={panelRef}
      data-testid="pitch-tagging-panel"
      data-status={
        isWaiting ? "waiting" : form.isHolding ? "holding" : "tagging"
      }
      data-activity-id={activity?.activityId}
      data-event-uuid={activity?.pitch.omnibusEventId}
      data-section={activeSection ?? undefined}
      className="flex h-full w-full flex-col bg-tagging-panel transition-colors"
    >
      <PitchHeader
        pitcher={matchup.pitcher}
        hitter={matchup.hitter}
        pitcherColor={pitcherColor}
        hitterColor={hitterColor}
        // The suggestion shows while the hold keeps it out of the form
        pitchType={formatPitchType(
          tags.pitchType ?? form.suggestions.pitchType,
          sport,
        )}
        pitchData={activity?.pitch.pitchData ?? null}
        hitData={activity?.pitch.hitData ?? null}
        sport={sport}
        isWaiting={isWaiting}
        view={view}
        onToggleView={() => setView(view === "tags" ? "metrics" : "tags")}
        onEndGame={onEndGame}
      />

      {view === "metrics" && activity ? (
        <PitchMetrics activity={activity} sport={sport} />
      ) : (
        <>
          {/* Sections */}
          <div className="tagging-scrollbar flex-1 overflow-y-auto">
            <div className={cn(CONTENT_WIDTH, "p-6")}>
              <TagSections
                form={form}
                isLoading={isWaiting || form.isHolding}
                sport={sport}
              />
            </div>
          </div>

          {/* Footer: nothing to save until the pitch arrives */}
          {activity && (
            <div
              className={cn(
                // Border
                "border-t border-tagging-option-border",
                // Background
                "bg-tagging-panel",
              )}
            >
              <div
                className={cn(
                  CONTENT_WIDTH,
                  "flex items-center justify-between px-6 py-4",
                )}
              >
                <button
                  type="button"
                  data-testid="pitch-tagging-reset-tags-button"
                  onClick={() => form.reset(activity)}
                  className="text-sm font-semibold text-gray-900 underline hover:no-underline dark:text-white"
                >
                  Reset Tags
                </button>
                <Button
                  data-testid="pitch-tagging-save-tags-button"
                  aria-busy={isSaveDisabled}
                  variant="secondary-flat-light"
                  onClick={save}
                  isDisabled={isSaveDisabled || form.isHolding}
                  isLoading={isSaveDisabled}
                  className="gap-2"
                >
                  Save Tags
                  <HotkeyBadge hotkey="Enter" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
